// "Build a report" — the main column of /reports/builder.
//
// The shell (sidebar, heading row) belongs to Reports.jsx, which renders this in place of
// a fixed report's panel. Everything this file decides is in report-builder.js beside it,
// where test/web/report-builder.test.js can reach it without a DOM; what is left here is
// markup, three fetches and the draft state.
//
// WHERE EACH LIST COMES FROM. The metrics, dimensions, grains, window fields, timezones,
// bounds AND the filter vocabulary all arrive from GET /api/reports/vocabulary, projected
// by src/reports/vocabulary.js from the frozen maps the compiler itself reads. Not one
// metric key, dimension key or grain is typed in this file. The filter rows are the
// admin console's ConditionBuilder, unchanged except for two props it needed to tell the
// truth about a report — a report's `filters` are the SAME object a saved view's
// conditions are, compiled by the same `compileConditions`, and two condition languages
// in one product is how they drift.
//
// NO CHARTING LIBRARY, for the reason written at the top of Reports.jsx: a <div> bar
// inherits the theme tokens and an SVG `fill` does not. `style={{ width }}` is the only
// inline style and it is unavoidable — the value is data.
//
// ZERO AND UNKNOWN ARE DIFFERENT VALUES, which is the rule the whole Reports tab is built
// on. A group the metric could not measure draws no bar and prints an em dash; it is
// never rendered as 0 and never ranked among the zeros.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { api } from '../../api/client.js';
import ConditionBuilder from '../../components/admin/ConditionBuilder.jsx';
import { formatDateTime } from '../../components/tickets/format.js';
import { useAtLeastRole } from '../../session/SessionProvider.jsx';
import { builderAvailable, parseConditionsDraft } from '../admin/view-conditions.js';
import { RANGE_PRESETS } from './report-query.js';
import {
  audienceChoices,
  audienceSentence,
  audienceToWire,
  breakdownLabel,
  breakdownsForMetric,
  builderNoteSentences,
  chartTypeFor,
  definitionToDraft,
  dimensionByKey,
  dimensionWarnings,
  dimensionsForMetric,
  draftProblems,
  draftToDefinition,
  draftWindow,
  isSubmittable,
  metricBars,
  metricFor,
  newAudienceChoice,
  newReportDraft,
  reportBuilderVocabulary,
  rowLabel,
  savedReportList,
  withBreakdown,
  withDimension,
  withMetric,
} from './report-builder.js';
import { supportPath } from '../../support-path.js';

const IDLE_SAVE = { status: 'idle', error: null, id: null };

/** Nothing loaded yet. `reports` and `queues` stay arrays in every state, so no consumer
 *  has to branch on a null before it maps. */
const EMPTY_LIST = { status: 'loading', reports: [], queues: [] };

const RANGE_LABELS = { '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', custom: 'Custom' };

/** Grain and window-field labels, derived mechanically so a value added server-side
 *  renders with a real name instead of blank — `fieldLabel`'s rule, one directory over. */
const humanise = (value) => String(value ?? '')
  .replace(/_/g, ' ')
  .replace(/ at$/, '')
  .replace(/^./, (c) => c.toUpperCase());

/** Today as a local 'YYYY-MM-DD', for resolving a preset into explicit dates. */
function todayLocal() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function Card({ children, className = '' }) {
  return <div className={`rounded-lg border border-line bg-surface p-4 ${className}`}>{children}</div>;
}

function Field({ label, htmlFor, children, className = '' }) {
  return (
    <div className={className}>
      <label className="field-label" htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}

/** The same note treatment the fixed reports use: the only warm hue in the system. */
function Notes({ sentences }) {
  if (sentences.length === 0) return null;
  return (
    <div className="rounded-lg border border-note-line bg-note p-3">
      <ul className="space-y-1">
        {sentences.map((sentence) => (
          <li key={sentence} className="text-sm text-note-ink">{sentence}</li>
        ))}
      </ul>
    </div>
  );
}

function Problems({ problems }) {
  if (problems.length === 0) return null;
  return (
    <ul className="grid gap-0.5">
      {problems.map((problem) => (
        <li key={`${problem.field ?? ''}:${problem.message}`} className="text-sm text-red-700 dark:text-red-300">
          {problem.message}
        </li>
      ))}
    </ul>
  );
}

/**
 * The grouping control, used for BOTH axes: the breakdown is drawn from the same served
 * dimensions, so it is the same control with a different list and a different empty option.
 *
 * Three option groups, because 105 entries in one flat list is a list nobody can find
 * anything in: the ticket's own columns, the custom fields this desk fills in, and the
 * retired ones. A RETIRED FIELD IS STILL OFFERED — ruling E5 — because a report over last
 * quarter must not break because the field was retired this quarter; it is filed under a
 * heading that says so rather than silently mixed in with the live ones.
 *
 * A stored dimension this desk no longer has is added as a disabled option carrying its
 * own key, so the screen can say WHICH grouping is missing instead of snapping the
 * control to something nobody chose. Same ruling `withStoredOptions` makes for a
 * condition value whose lookup missed.
 */
function DimensionSelect({ id, selected, vocabulary, offered, onChange, emptyLabel }) {
  const native = offered.filter((d) => d.kind === 'native');
  const custom = offered.filter((d) => d.kind !== 'native' && d.is_active !== false);
  const retired = offered.filter((d) => d.kind !== 'native' && d.is_active === false);
  const missing = selected !== '' && !offered.some((d) => d.key === selected);

  return (
    <select
      id={id}
      className="field-input"
      value={selected}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{emptyLabel}</option>
      {missing && (
        <option value={selected} disabled>
          {dimensionByKey(selected, vocabulary)?.label ?? selected} (not available)
        </option>
      )}
      <optgroup label="Ticket fields">
        {native.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
      </optgroup>
      {custom.length > 0 && (
        <optgroup label="Custom fields">
          {custom.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </optgroup>
      )}
      {retired.length > 0 && (
        <optgroup label="Retired custom fields">
          {retired.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </optgroup>
      )}
    </select>
  );
}

/**
 * The result.
 *
 * Both shapes are horizontal bars in a table, and the difference between them is the
 * ORDER, which `orderRows` decides: a date dimension stays chronological, because sorting
 * a time series by size draws September before August and calls it a trend; everything
 * else is largest first, because that is the question being asked.
 *
 * A BREAKDOWN ADDS A COLUMN, NOT A GRID. One row per cell the server sent, in the server's
 * order, with the group repeated down the first column — because a grid has a slot for
 * every pairing and would have to put something in the ones no ticket occupies, and the
 * only two candidates, "absent" and "zero", are the distinction this table exists to keep.
 * An unmeasured cell is the same em dash a group has always been, never a 0.
 */
export function ResultTable({ payload, vocabulary }) {
  const metric = metricFor(payload.meta.metric, vocabulary);
  const dimension = dimensionByKey(payload.meta.dimension, vocabulary);
  // Read off the RESULT's meta, never off the draft: this table describes the definition
  // that produced these rows, which is not necessarily the one on screen.
  const breakdownKey = payload.meta.breakdown ?? null;
  const breakdown = breakdownKey ? dimensionByKey(breakdownKey, vocabulary) : null;
  const chartType = chartTypeFor(payload.meta.dimension, vocabulary);
  const bars = metricBars(payload.data.rows, metric, chartType, Boolean(breakdownKey));

  if (bars.length === 0) {
    return (
      <Card>
        <p className="text-sm text-ink-muted">
          No ticket in this window matched. Only groups with at least one ticket are listed,
          so an empty report means nothing matched — not that every group measured zero.
        </p>
      </Card>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <table className="w-full table-fixed divide-y divide-line text-sm">
        <colgroup>
          <col className="w-[14rem]" />
          {breakdownKey && <col className="w-[12rem]" />}
          <col />
          <col className="w-[7rem]" />
          <col className="w-[6rem]" />
        </colgroup>
        <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
          <tr>
            <th scope="col" className="px-4 py-2.5">{dimension?.label ?? 'Group'}</th>
            {breakdownKey && (
              <th scope="col" className="px-4 py-2.5">{breakdown?.label ?? breakdownKey}</th>
            )}
            {/* The bar is a second, visual encoding of the number in the next column, so
                its heading is for screen readers only — printing the metric's name twice
                side by side reads as two different measurements. */}
            <th scope="col" className="px-4 py-2.5">
              <span className="sr-only">{metric?.label ?? 'Value'}, as a bar</span>
            </th>
            <th scope="col" className="px-4 py-2.5 text-right">{metric?.label ?? 'Value'}</th>
            {/* The denominator, beside every figure and never omitted. An average over
                four tickets must not look like an average over four hundred. For a count
                metric it repeats the value, which is honest rather than redundant: it is
                what says the figure is a ticket count and not a rate. */}
            <th scope="col" className="px-4 py-2.5 text-right">Tickets</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {bars.map((bar) => (
            <tr key={bar.key} className="group transition-colors hover:bg-sunken">
              <td className="truncate px-4 py-2 text-ink" title={rowLabel(bar.row, dimension)}>
                {rowLabel(bar.row, dimension)}
              </td>
              {breakdownKey && (
                <td
                  className="truncate px-4 py-2 text-ink-secondary"
                  title={breakdownLabel(bar.row, breakdown)}
                >
                  {breakdownLabel(bar.row, breakdown)}
                </td>
              )}
              <td className="px-4 py-2">
                {/* aria-hidden: the number is in the <td> beside it, so the bar has
                    nothing of its own to announce. */}
                <div aria-hidden="true" className="flex h-2 w-full overflow-hidden rounded bg-sunken">
                  <div className="bg-brand-600" style={{ width: `${bar.width}%` }} />
                </div>
              </td>
              <td className={`px-4 py-2 text-right tabular-nums ${bar.measured ? 'text-ink' : 'text-ink-muted'}`}>
                {bar.formatted}
                {!bar.measured && (
                  <span className="sr-only"> no measurement for this group</span>
                )}
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-ink-secondary">
                {bar.sample.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function ReportBuilder() {
  const [searchParams] = useSearchParams();
  const savedId = searchParams.get('saved') ?? '';
  const today = todayLocal();

  const [vocabulary, setVocabulary] = useState({ status: 'loading', value: null, error: null });
  const [draft, setDraft] = useState(null);
  const [result, setResult] = useState({ status: 'idle', payload: null, error: null });
  const [saved, setSaved] = useState(null);
  const [jsonFilters, setJsonFilters] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [saveState, setSaveState] = useState(IDLE_SAVE);
  // Shared, until an agent says otherwise. That is what every report saved before this
  // control existed was, so the default changes nothing about an unattended Save.
  // Lead+ may publish a report to the desk or to a queue; everyone licensed may keep one
  // private to themselves (user decision, 2026-10-05). Read once here and passed to both
  // audience functions, so the control and its default cannot disagree about it.
  const canPublish = useAtLeastRole('support', 'lead');
  const [audience, setAudience] = useState(() => newAudienceChoice(canPublish));
  const [savedList, setSavedList] = useState(EMPTY_LIST);

  // One fetch per screen. The vocabulary is a read with no arguments, and nothing on this
  // screen can change it.
  useEffect(() => {
    const controller = new AbortController();
    api.get('/reports/vocabulary', { signal: controller.signal })
      .then((payload) => {
        const value = reportBuilderVocabulary(payload?.data);
        setVocabulary({ status: value ? 'ready' : 'error', value, error: null });
        if (value) setDraft((current) => current ?? newReportDraft(value));
      })
      .catch((error) => {
        if (controller.signal.aborted || error.name === 'AbortError') return;
        setVocabulary({ status: 'error', value: null, error });
      });
    // Empty deps with nothing omitted: everything this effect closes over is either a
    // module-scope function or a setter, both of which are stable.
    return () => controller.abort();
  }, []);

  // The saved reports this agent may see, AND the queues they may scope one to. One
  // request, because GET /reports/saved answers both from the same `queue_members` read
  // — the queues that make a queue-scoped report visible to you are exactly the queues
  // you may put one in.
  //
  // THE LIST IS FILTERED BY THE SERVER, through the same audience clause the run
  // endpoint uses. Nothing here narrows it and nothing here could: this screen never
  // learns that a report it cannot see exists.
  const loadSavedReports = useCallback((signal) => api
    .get('/reports/saved', signal ? { signal } : undefined)
    .then((payload) => {
      const read = savedReportList(payload);
      // A payload this build cannot read is an ERROR, not an empty desk. The two look
      // identical on screen unless the status says which it is.
      setSavedList(read
        ? { status: 'ready', reports: read.reports, queues: read.queues }
        : { status: 'error', reports: [], queues: [] });
    })
    .catch((error) => {
      if (signal?.aborted || error.name === 'AbortError') return;
      setSavedList({ status: 'error', reports: [], queues: [] });
    }), []);

  useEffect(() => {
    const controller = new AbortController();
    loadSavedReports(controller.signal);
    return () => controller.abort();
  }, [loadSavedReports]);

  // Opening a SAVED report. This is the one path that reads a stored row's audience:
  // POST /run takes a definition from the body and never consults one, so a report the
  // viewer may not see is a 404 here and nowhere else.
  useEffect(() => {
    const value = vocabulary.value;
    if (!value || savedId === '') return undefined;
    const controller = new AbortController();
    setResult({ status: 'loading', payload: null, error: null });
    api.post(`/reports/saved/${encodeURIComponent(savedId)}/run`, undefined, { signal: controller.signal })
      .then((payload) => {
        setResult({ status: 'ready', payload, error: null });
        const stored = payload?.meta?.saved_report ?? null;
        setSaved(stored);
        // Seeded from the STORED definition rather than from `meta`, which carries the
        // metric, dimension, grain and window but not the filters — a draft seeded from
        // `meta` would quietly drop them and then offer to re-run a different report.
        if (stored?.definition) setDraft(definitionToDraft(stored.definition, value));
      })
      .catch((error) => {
        if (controller.signal.aborted || error.name === 'AbortError') return;
        setResult({ status: 'error', payload: null, error });
      });
    return () => controller.abort();
  }, [savedId, vocabulary.value]);

  const value = vocabulary.value;
  const metric = draft && value ? metricFor(draft.metric, value) : null;
  const dimension = draft && value ? dimensionByKey(draft.dimension, value) : null;
  // Keyed on the METRIC rather than on the whole draft: the offered list depends on
  // nothing else, and recomputing 105 entries on every keystroke in the name box is work
  // for nothing. The lint rule wants the whole `draft` here and would be wrong.
  const offered = useMemo(
    () => (draft && value ? dimensionsForMetric(draft.metric, value) : []),
    [draft?.metric, value], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // The same list less the grouping already chosen, so the one pair the server refuses
  // cannot be built by the control. Keyed on the metric AND the dimension for the reason
  // written above.
  const breakdowns = useMemo(
    () => (draft && value ? breakdownsForMetric(draft.metric, draft.dimension, value) : []),
    [draft?.metric, draft?.dimension, value], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const breakdown = draft && value ? dimensionByKey(draft.breakdown, value) : null;
  const problems = draft ? draftProblems(draft, value, today) : [];
  const ready = draft ? isSubmittable(draft, value, today) : false;
  const warnings = draft && value ? dimensionWarnings(draft, value) : [];
  const activeWindow = draft ? draftWindow(draft, today) : null;
  // ONE grain for the report, shown when EITHER axis is a date, because the definition
  // carries one `grain` and the compiler buckets both axes with it.
  const showGrain = dimension?.grainable === true || breakdown?.grainable === true;
  const showWindow = metric ? (metric.applies_window || showGrain) : true;

  // Editing anything makes the result stale: it describes the definition that produced
  // it, not the one on screen. Dropping the saved name at the same time is deliberate —
  // an edited draft is no longer that saved report, and captioning it with that name
  // would be the caption lying about the number under it.
  const patch = useCallback((next) => {
    setDraft(next);
    setSaved(null);
    // "Saved." must not sit under a draft that has since been changed; what was saved is
    // the definition at the moment of the POST, not whatever is on screen now.
    setSaveState(IDLE_SAVE);
  }, []);

  const save = () => {
    const name = saveName.trim();
    // Null means the audience is not finished — a queue scope with no queue chosen. The
    // button is already disabled on the same condition; this is the second half of that
    // rule rather than a different one.
    const wire = audienceToWire(audience, savedList.queues);
    if (!ready || name === '' || wire === null) return;
    setSaveState({ status: 'saving', error: null, id: null });
    // POST /api/reports/saved, not the admin descriptor this used to call. That endpoint
    // cannot write an audience: its `coerce` sees neither the caller nor the database, so
    // "private to me" has no id to resolve and a queue has no membership to check — and
    // its `loadSql` carries `owner_user_id IS NULL`, so it could not even read back a
    // private row it had just inserted. The new endpoint validates the definition with
    // the same `validateDefinition`, writes the same `config_events` row under the same
    // `saved_report` resource, and holds the same `lead` floor. The reasoning is written
    // out at the endpoint itself.
    api.post('/reports/saved', { name, definition: draftToDefinition(draft, today, value), audience: wire })
      .then((payload) => {
        setSaveState({ status: 'saved', error: null, id: payload?.data?.id ?? null });
        // The list this screen renders now has one more row in it. Re-read rather than
        // spliced: the server decides what is in it, and a client that appended its own
        // row would be asserting a visibility it did not check.
        loadSavedReports();
      })
      .catch((error) => setSaveState({ status: 'error', error, id: null }));
  };

  const run = () => {
    if (!ready) return;
    const definition = draftToDefinition(draft, today, value);
    setResult({ status: 'loading', payload: null, error: null });
    api.post('/reports/run', definition)
      .then((payload) => setResult({ status: 'ready', payload, error: null }))
      .catch((error) => setResult({ status: 'error', payload: null, error }));
  };

  if (vocabulary.status === 'loading') {
    return <Card><p className="text-sm text-ink-muted">Loading…</p></Card>;
  }
  if (vocabulary.status === 'error' || !value || !draft) {
    return (
      <Card>
        <p className="text-sm font-medium text-ink">A report cannot be built right now.</p>
        <p className="mt-1 text-sm text-ink-secondary">
          The list of metrics and dimensions could not be loaded.
          {vocabulary.error?.message ? ` ${vocabulary.error.message}` : ''} Reload the page to try again.
        </p>
      </Card>
    );
  }

  const filtersVocabulary = value.filters ?? null;
  const canUseRowBuilder = builderAvailable(draft.filters, filtersVocabulary) && jsonFilters === null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold tracking-tight">
          {saved ? saved.name : 'Build a report'}
        </h1>
        {saved && (
          <p className="text-sm text-ink-muted">Saved report · edit below to ask something else</p>
        )}
      </div>

      <Card className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Measure" htmlFor="report-metric">
            <select
              id="report-metric"
              className="field-input"
              value={draft.metric}
              // `withMetric`, not a reshape written out here: what happens to the
              // aggregate and to a dimension the new metric refuses is a rule, it lives
              // in report-builder.js, and a test asserts it. A second copy in a change
              // handler is a second rule.
              onChange={(event) => patch(withMetric(draft, event.target.value, value))}
            >
              {value.metrics.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </Field>

          {/* Only where there is a choice: a count has one aggregate, and a control with
              one option is furniture that looks like a decision. */}
          {metric && metric.aggregates.length > 1 && (
            <Field label="Summarised as" htmlFor="report-aggregate">
              <select
                id="report-aggregate"
                className="field-input"
                value={draft.aggregate}
                onChange={(event) => patch({ ...draft, aggregate: event.target.value })}
              >
                {metric.aggregates.map((a) => <option key={a} value={a}>{humanise(a)}</option>)}
              </select>
            </Field>
          )}

          <Field label="Grouped by" htmlFor="report-dimension">
            <DimensionSelect
              id="report-dimension"
              selected={draft.dimension}
              emptyLabel="Choose a grouping…"
              vocabulary={value}
              offered={offered}
              onChange={(key) => patch(withDimension(draft, key, value))}
            />
          </Field>

          {/* OPTIONAL, and offered only once there is a first grouping to break down: a
              second grouping with no first one is not a report anyone asked for. Its empty
              option is "No breakdown", a finished choice rather than an unmade one — which
              is why it does not read "Choose…" like the control above it. */}
          {draft.dimension !== '' && (
            <Field label="Broken down by" htmlFor="report-breakdown">
              <DimensionSelect
                id="report-breakdown"
                selected={draft.breakdown}
                emptyLabel="No breakdown"
                vocabulary={value}
                offered={breakdowns}
                onChange={(key) => patch(withBreakdown(draft, key, value))}
              />
            </Field>
          )}

          {showGrain && (
            <Field label="Bucket" htmlFor="report-grain">
              <select
                id="report-grain"
                className="field-input"
                value={draft.grain}
                onChange={(event) => patch({ ...draft, grain: event.target.value })}
              >
                {value.grains.map((g) => <option key={g} value={g}>{humanise(g)}</option>)}
              </select>
            </Field>
          )}
        </div>

        {showWindow && (
          <div className="flex flex-wrap items-end gap-3 border-t border-line pt-3">
            <Field label="Date measured" htmlFor="report-window-field">
              <select
                id="report-window-field"
                className="field-input"
                value={draft.windowField}
                onChange={(event) => patch({ ...draft, windowField: event.target.value })}
              >
                {value.window_fields.map((f) => <option key={f} value={f}>{humanise(f)}</option>)}
              </select>
            </Field>
            <Field label="Range" htmlFor="report-range">
              <select
                id="report-range"
                className="field-input"
                value={draft.range}
                onChange={(event) => patch({ ...draft, range: event.target.value })}
              >
                {RANGE_PRESETS.map((p) => <option key={p} value={p}>{RANGE_LABELS[p]}</option>)}
              </select>
            </Field>
            {draft.range === 'custom' && (
              <>
                <Field label="From" htmlFor="report-from">
                  <input
                    id="report-from" type="date" className="field-input" value={draft.from}
                    onChange={(event) => patch({ ...draft, from: event.target.value })}
                  />
                </Field>
                <Field label="To" htmlFor="report-to">
                  <input
                    id="report-to" type="date" className="field-input" value={draft.to}
                    onChange={(event) => patch({ ...draft, to: event.target.value })}
                  />
                </Field>
              </>
            )}
            <Field label="Timezone" htmlFor="report-tz">
              <select
                id="report-tz"
                className="field-input"
                value={draft.tz}
                onChange={(event) => patch({ ...draft, tz: event.target.value })}
              >
                {value.timezones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </Field>
            {activeWindow && (
              <p className="pb-2 text-xs text-ink-muted">{activeWindow.from} to {activeWindow.to}</p>
            )}
          </div>
        )}

        <div className="border-t border-line pt-3">
          <p className="field-label">Filters</p>
          {canUseRowBuilder ? (
            <ConditionBuilder
              id="report-filters"
              value={draft.filters}
              vocabulary={filtersVocabulary}
              onChange={(filters) => patch({ ...draft, filters })}
              onShowJson={() => setJsonFilters(JSON.stringify(draft.filters, null, 2))}
              // A report with no filters is bounded by its window and is the ordinary
              // case; a VIEW with none shows the entire desk, which is why that builder
              // refuses it. See the props' own comment in ConditionBuilder.jsx.
              allowEmpty
              brokenMessage={'One of the rows below names a filter this desk does not have. '
                + 'This report cannot be run until it is fixed or removed.'}
            />
          ) : (
            <JsonFilters
              text={jsonFilters ?? JSON.stringify(draft.filters, null, 2)}
              available={builderAvailable(draft.filters, filtersVocabulary)}
              onText={setJsonFilters}
              onCommit={(filters) => { patch({ ...draft, filters }); setJsonFilters(null); }}
              onCancel={builderAvailable(draft.filters, filtersVocabulary) ? () => setJsonFilters(null) : null}
            />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-line pt-3">
          <button type="button" className="btn-primary" disabled={!ready || result.status === 'loading'} onClick={run}>
            {result.status === 'loading' ? 'Running…' : 'Run report'}
          </button>
          <Problems problems={problems} />
        </div>

        {/* SHOWN TO EVERYONE LICENSED, and it used to be lead-only. A user decision of
            2026-10-05 is that an agent may keep a report private to themselves; what
            stays a lead's is PUBLISHING one, to the desk or to a queue. So the control
            that narrows is the audience below, not this whole block.
            `assertMayPublish` in src/routes/api/reports.js is the enforcement and refuses
            regardless of what this renders (SPEC.md §3.3). This block is here at all
            because the console has no control for a definition object — this screen is
            where one is authored, so this is where one is saved. */}
        <div className="border-t border-line pt-3">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Save this report as" htmlFor="report-save-name" className="min-w-0 flex-1">
              <input
                id="report-save-name"
                type="text"
                className="field-input w-full"
                maxLength={200}
                placeholder="A name the desk will recognise"
                value={saveName}
                onChange={(event) => { setSaveName(event.target.value); setSaveState(IDLE_SAVE); }}
              />
            </Field>
            {/* ONE CHOICE, NEVER TWO HALVES OF ONE. The schema holds the audience as
                two nullable columns with a CHECK forbidding both (migration 038);
                offering an owner box and a queue box would make that CHECK the thing
                that reports an error to an agent. A single `kind` cannot ask for
                both.

                Rendered only where there IS a choice — the same rule the aggregate
                control above follows. An agent who may not publish has exactly one
                audience available, and a select with one option is furniture that looks
                like a decision; the caption below says what will happen instead. */}
            {audienceChoices(savedList.queues, canPublish).length > 1 && (
              <Field label="Who can see it" htmlFor="report-save-audience">
                <select
                  id="report-save-audience"
                  className="field-input"
                  value={audience.kind}
                  onChange={(event) => {
                    setAudience({ ...audience, kind: event.target.value });
                    setSaveState(IDLE_SAVE);
                  }}
                >
                  {/* The queue option is absent for an agent in no queue — the list is
                      the caller's OWN memberships, served with the saved reports. */}
                  {audienceChoices(savedList.queues, canPublish).map((choice) => (
                    <option key={choice.value} value={choice.value}>{choice.label}</option>
                  ))}
                </select>
              </Field>
            )}
            {audience.kind === 'queue' && (
              <Field label="Which queue" htmlFor="report-save-queue">
                <select
                  id="report-save-queue"
                  className="field-input"
                  value={audience.queue_id}
                  onChange={(event) => {
                    setAudience({ ...audience, queue_id: event.target.value });
                    setSaveState(IDLE_SAVE);
                  }}
                >
                  {/* Blank first, and it is not a valid choice: a pre-selected queue
                      would publish to whichever queue happened to sort first. */}
                  <option value="">Choose a queue…</option>
                  {savedList.queues.map((queue) => (
                    <option key={queue.id} value={queue.id}>{queue.name}</option>
                  ))}
                </select>
              </Field>
            )}
            <button
              type="button"
              className="btn-ghost mb-0.5 ring-1 ring-line-strong"
              disabled={!ready || saveName.trim() === '' || saveState.status === 'saving'
                || audienceToWire(audience, savedList.queues) === null}
              onClick={save}
            >
              {saveState.status === 'saving' ? 'Saving…' : 'Save'}
            </button>
          </div>
          {/* What the choice above MEANS, in the same words the saved list uses for an
              existing report — one function, so the promise made at Save and the
              statement made in the list cannot drift apart. */}
          <p className="mt-1 text-xs text-ink-muted">
            {audienceToWire(audience, savedList.queues) === null
              ? 'Choose a queue to share this report with.'
              // Joined with a separator rather than folded into a sentence, so the
              // queue's own name keeps the capitals its name has.
              : `Once saved · ${audienceSentence(audience, savedList.queues)}`}
          </p>
          {/* Why there was no choice to make. Said plainly rather than left as an absent
              control: an agent who expected to be able to share one should be told it is
              a lead's to do, not left wondering where the option went. */}
          {!canPublish && (
            <p className="mt-1 text-xs text-ink-muted">
              Sharing a report with the desk or with a queue is a lead’s to do.
            </p>
          )}
          {canPublish && savedList.status === 'error' && (
            <p className="mt-1 text-xs text-ink-muted">
              Your queues could not be loaded, so sharing with a queue is not offered.
              Reload the page to try again.
            </p>
          )}
          {saveState.status === 'saved' && (
            <p className="mt-1 text-xs text-ink-secondary">
              Saved.{' '}
              {saveState.id && (
                <Link className="underline" to={supportPath(`/reports/builder?saved=${encodeURIComponent(saveState.id)}`)}>
                  Open it by its own link
                </Link>
              )}
            </p>
          )}
          {saveState.status === 'error' && (
            <p className="mt-1 text-xs text-red-700 dark:text-red-300">
              {saveState.error?.message ?? 'It could not be saved.'}
            </p>
          )}
        </div>
      </Card>

      {/* Said BEFORE the report runs, from the choice alone. The server repeats them with
          the rows; this is what stops a multiselect total being read as a ticket count for
          the seconds before the response arrives. */}
      <Notes sentences={warnings} />

      {result.status === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            This report could not be run.
          </p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">{result.error?.message}</p>
        </div>
      )}

      {result.status === 'ready' && result.payload && (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink">
              {metricFor(result.payload.meta.metric, value)?.label ?? result.payload.meta.metric}
              {' by '}
              {dimensionByKey(result.payload.meta.dimension, value)?.label ?? result.payload.meta.dimension}
            </h2>
            {/* Echoed from the response, never recomputed here: a caption describing a
                window nobody asked for is the same class of lie as a wrong number. */}
            <p className="text-sm text-ink-muted">
              {result.payload.meta.window
                ? `${result.payload.meta.window.from} to ${result.payload.meta.window.to} · ${result.payload.meta.window.tz}`
                : `As at ${formatDateTime(result.payload.meta.generated_at)}`}
            </p>
          </div>
          <ResultTable payload={result.payload} vocabulary={value} />
          <Notes sentences={builderNoteSentences(result.payload.meta.notes)} />
        </>
      )}

      <SavedReports list={savedList} openId={savedId} vocabulary={value} />
    </div>
  );
}

/** "Ticket count by Channel", from the served vocabulary, falling back to the stored
 *  keys. The fallback is the same one the result heading above uses and is reachable for
 *  the same reason: a saved report may be grouped by a custom field that has since been
 *  DELETED, which the vocabulary then does not offer at all (a merely RETIRED one is
 *  still served, by ruling E5), and a blank would hide what the report holds.
 *
 *  EITHER being null means this says nothing, rather than printing "null by Channel".
 *  `validateDefinition` requires both of a report that was saved through the endpoint,
 *  so the only way here is a row whose definition would not parse — which the list
 *  serves with both as null — or one written straight to the table. */
function measuredBy(report, vocabulary) {
  if (report.metric === null || report.dimension === null) return null;
  const metric = metricFor(report.metric, vocabulary)?.label ?? report.metric;
  const dimension = dimensionByKey(report.dimension, vocabulary)?.label ?? report.dimension;
  return `${metric} by ${dimension}`;
}

/**
 * The saved reports this agent may open.
 *
 * NOT GATED ON RANK, and that is the point of it: saving a report is a configuration act
 * and is lead+, but a report that has been shared with the desk is of no use if only the
 * person who wrote it can find it. Every row here reached the browser through the
 * server's audience clause — this component does no filtering and knows of no report it
 * is not rendering.
 */
function SavedReports({ list, openId, vocabulary }) {
  if (list.status === 'loading') return null;
  if (list.status === 'error') {
    return (
      <Card>
        <h2 className="text-sm font-semibold text-ink">Saved reports</h2>
        {/* An empty desk and a failed read look identical unless one of them says so. */}
        <p className="mt-1 text-sm text-ink-secondary">
          The list of saved reports could not be loaded. Reload the page to try again.
        </p>
      </Card>
    );
  }
  return (
    <Card>
      <h2 className="text-sm font-semibold text-ink">Saved reports</h2>
      {list.reports.length === 0 ? (
        <p className="mt-1 text-sm text-ink-muted">
          Nothing saved yet. Build a report above and give it a name.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-line">
          {list.reports.map((report) => (
            <li key={report.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
              <div className="min-w-0">
                {/* The report already open is not a link to itself. */}
                {report.id === openId
                  ? <span className="text-sm font-medium text-ink">{report.name}</span>
                  : (
                    <Link
                      className="text-sm font-medium text-ink underline"
                      to={supportPath(`/reports/builder?saved=${encodeURIComponent(report.id)}`)}
                    >
                      {report.name}
                    </Link>
                  )}
                <p className="text-xs text-ink-muted">
                  {measuredBy(report, vocabulary)
                    ? `${measuredBy(report, vocabulary)} · ` : ''}
                  {audienceSentence(report.audience, list.queues)}
                  {report.updated_at ? ` · ${formatDateTime(report.updated_at)}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * The floor, and it stays permanently reachable — the same ruling the admin console makes
 * about its own conditions editor. It is the only editor available when the stored
 * filters are a shape the row builder cannot express, and a report holding one must not
 * become both uneditable and unviewable.
 *
 * Text the editor cannot read BLOCKS the change rather than resolving to `{}`: `{}` is a
 * conditions object with neither group, which `compileConditions` throws on.
 */
function JsonFilters({ text, available, onText, onCommit, onCancel }) {
  const parsed = parseConditionsDraft(text);
  return (
    <div className="grid gap-2">
      {!available && (
        <p className="text-xs text-ink-secondary">
          These filters are not a shape the row builder can show, so they are edited as JSON.
        </p>
      )}
      <textarea
        className="field-input font-mono text-xs"
        rows={6}
        value={text}
        onChange={(event) => onText(event.target.value)}
      />
      {!parsed.ok && <p className="text-xs text-red-700 dark:text-red-300">{parsed.error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-ghost px-2 py-0.5 text-xs ring-1 ring-line-strong"
          disabled={!parsed.ok}
          onClick={() => parsed.ok && onCommit(parsed.value)}
        >
          Use these filters
        </button>
        {onCancel && (
          <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={onCancel}>
            Back to the row builder
          </button>
        )}
      </div>
    </div>
  );
}
